import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIdleTimer } from 'react-idle-timer';
import _ from 'lodash';

import { RootState } from 'src/configs/store';
import AppModeContext from './AppMode.context';

interface AppModeProps {
  children: React.ReactNode
}

function AppMode({ children }: AppModeProps) {
  const [isRetail, setIsRetail] = useState(() => {
    if (process.env.REACT_APP_RETAIL === 'true') {
      localStorage.setItem('retail', 'true');
      return true;
    } else if (process.env.REACT_APP_RETAIL !== 'true' && process.env.REACT_APP_ENV === 'production') {
      return false;
    } else {
      return localStorage.getItem('retail') === 'true';
    }
  });
  const [isTrading, setIsTrading] = useState(() => {
    if (process.env.REACT_APP_TRADING === 'true') {
      return true;
    } else if (process.env.REACT_APP_TRADING !== 'true' && process.env.REACT_APP_ENV === 'production') {
      return false;
    } else {
      return localStorage.getItem('trading') === 'true';
    }
  });

  const { settings } = useSelector((state: RootState) => state.content);
  const { tillUser } = useSelector((state: RootState) => state.auth);
  const { tradingUser } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();

  const { pause, reset } = useIdleTimer({
    startManually: (!isRetail || _.isNull(tillUser)) || (!isTrading || _.isNull(tradingUser)),
    timeout: settings.loginTimeout,
    onIdle: () => {
      if (isRetail && !_.isNull(tillUser)) {
        dispatch.auth.tillLogout({ showInactivityMessage: true, hardLogout:false, expired:false  });
      } else if (isTrading && !_.isNull(tradingUser)) {
        dispatch.auth.tradingLogout(true);
      }
    },
    debounce: 500
  });

  const context = useMemo(() => {
    return {
      RETAIL_MODE: isRetail,
      TRADING_MODE: isTrading
    };
  }, [isRetail, isTrading]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const getRetail = query.get('retail');
    const getTrading = query.get('trading');
    const getTestSystem = query.get('testSystem');

    if (getRetail) {
      localStorage.setItem('retail', getRetail);
      localStorage.removeItem('trading');
      dispatch.auth.tradingLogout(false);

      if (getRetail === 'false') {
        dispatch.auth.tillLogout({ showInactivityMessage: false, hardLogout:true, expired:false  });
      }

      if (process.env.REACT_APP_RETAIL !== 'true' && process.env.REACT_APP_ENV !== 'production') {
        setIsRetail(getRetail === 'true');
      }
    } else if (getTrading) {
      localStorage.setItem('trading', getTrading);
      localStorage.removeItem('retail');
      dispatch.auth.tillLogout({ showInactivityMessage: false, hardLogout:true, expired:false });

      if (getTrading === 'false') {
        dispatch.auth.tradingLogout(false);
      }

      if (process.env.REACT_APP_TRADING !== 'true' && process.env.REACT_APP_ENV !== 'production') {
        setIsTrading(getTrading === 'true');
      }
    } else if (getTestSystem) {
      localStorage.setItem('testSystem', getTestSystem);
    }
  }, [dispatch.auth]);

  useMemo(() => {
    if ((!isRetail || _.isNull(tillUser)) && (!isTrading || _.isNull(tradingUser))) {
      pause();
    } else {
      reset();
    }
  }, [isRetail, tillUser, isTrading, tradingUser, pause, reset]);

  useMemo(() => {
    if (isRetail === true) {
      dispatch.peripherals.getLongPoll();
    }
  }, [isRetail]);

  return (
    <AppModeContext.Provider value={ context }>
      { children }
    </AppModeContext.Provider>
  );
}

export default React.memo(AppMode);
